import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls a focused control into view for keyboard / assistive-technology users
 * (originally added for TalkBack, NA-6001) without hijacking pointer taps.
 *
 * A tap on a focusable listing tile also fires `focusin`; previously the handler
 * queued a deferred `scrollIntoView` that raced with route navigation and left
 * mobile users at the bottom of the destination page (APS-2013). We now only
 * scroll when the last interaction came from the keyboard, and we cancel any
 * pending scroll when the route changes so a queued call cannot run against the
 * next page.
 */
const useAssistiveFocusScroll = () => {
  const location = useLocation();
  const lastInputWasPointerRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handlePointerDown = () => {
      lastInputWasPointerRef.current = true;
    };

    const handleKeyDown = () => {
      lastInputWasPointerRef.current = false;
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (lastInputWasPointerRef.current) {
        return;
      }

      const target = event.target as HTMLElement | null;

      if (target && typeof target.scrollIntoView === "function") {
        const scheduledPathname = location.pathname;

        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;

          // Bail if navigation happened before the deferred scroll ran.
          if (window.location.pathname !== scheduledPathname) {
            return;
          }

          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 0); // small delay to prevent skipping
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("focusin", handleFocusIn);

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname]);
};

export default useAssistiveFocusScroll;
